import appConfig from '@/configs/app.config'
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import i18n from 'i18next'
import { dateLocales } from '@/locales'
import dayjs from 'dayjs'

/** The languages the interface speaks (the user, 11 Oct). */
export const UI_LANGUAGES = ["en", "fr", "es"] as const;

/**
 * A first visit speaks the browser's language when it is one of ours; once a
 * language is chosen, the saved choice wins (persist restores it over this).
 */
function firstLanguage(): string {
    try {
        for (const l of navigator.languages ?? [navigator.language]) {
            const base = (l || '').slice(0, 2).toLowerCase()
            if ((UI_LANGUAGES as readonly string[]).includes(base)) return base
        }
    } catch {
        // No navigator (tests, server): the default.
    }
    return appConfig.locale
}

type LocaleState = {
    currentLang: string
    setLang: (payload: string) => void
}

export const useLocaleStore = create<LocaleState>()(
    devtools(
        persist(
            (set) => ({
                currentLang: firstLanguage(),
                setLang: (lang: string) => {
                    const formattedLang = lang.replace(
                        /-([a-z])/g,
                        function (g) {
                            return g[1].toUpperCase()
                        },
                    )

                    i18n.changeLanguage(formattedLang)

                    dateLocales[formattedLang]().then(() => {
                        dayjs.locale(formattedLang)
                    })

                    return set({ currentLang: lang })
                },
            }),
            { name: 'locale' },
        ),
    ),
)
